/**
 * Words and small rules for the warm-up panel of the Ads Manager and for the refusals of budget changes.
 */

export const WARMUP_STATE_LABELS = Object.freeze({
    planned: 'Starting',
    warming: 'Warming up',
    awaiting_confirmation: 'Waiting for your confirmation',
    expanding: 'Expanding',
    expanded: 'Expanded',
    failed: 'Expansion stopped',
    cancelled: 'Cancelled',
})

export const WARMUP_STATE_NOTES = Object.freeze({
    planned: 'The campaign is being published. The warm-up starts as soon as its ad set is on Meta.',
    warming: 'The warm-up ad set collects pixel events. Your audience ad sets are paused and not published yet.',
    awaiting_confirmation: 'Nothing changes on Meta until you confirm.',
    expanding:
        'Turba is creating your audience ad sets and switching them on. Changes to this campaign wait until it has finished.',
    expanded: 'The audience ad sets were created and switched on. The warm-up ad set is paused.',
    failed: 'The expansion stopped before it finished. Your audience ad sets stay paused.',
    cancelled: 'The warm-up was cancelled.',
})

/** The state shown to the owner: an expansion whose lease ran out was interrupted. */
export const warmupStateLabel = (warmup) =>
    warmup.state === 'expanding' && !warmup.expansion?.lease_live
        ? 'Expansion interrupted'
        : WARMUP_STATE_LABELS[warmup.state] || warmup.state

export const warmupStateNote = (warmup) =>
    warmup.state === 'expanding' && !warmup.expansion?.lease_live
        ? 'The expansion was cut off. Resume it to continue where it stopped, or cancel the warm-up.'
        : WARMUP_STATE_NOTES[warmup.state] || ''

export const AUTOMATED_NOT_ALLOWED_NOTE = 'Automated mode is not available for your licence.'

export const PAUSED_CAMPAIGN_NOTE = 'Campaign paused: the new ad sets will start when you resume it.'

// What the last check says
const CHECK_REASON_TEXT = Object.freeze({
    below_threshold: 'The warm-up has not reached the threshold yet.',
    threshold_met: 'The warm-up has reached the threshold.',
    stalled: 'The warm-up has not reached the threshold in 28 days.',
    declined_recently: 'You declined the expansion. Turba asks again after 7 days.',
    warmup_ad_set_not_on: 'The warm-up ad set is not switched on, so Turba does not expand automatically.',
    halted: 'Automatic steps are paused for all campaigns right now.',
    switch_off: 'The pixel warm-up is switched off for your licence.',
    needs_reauth: 'Your Meta connection needs to be reconnected.',
    rate_limited: 'Meta asked Turba to slow down. The next check will try again.',
    connection_unusable: 'Your ad account connection could not be used.',
    ad_account_unreadable: 'The campaign could not be read with your connection.',
    read_failed: 'Meta could not be read. The next check will try again.',
    warmup_ad_set_not_listed: 'The warm-up ad set was not found on Meta.',
})

export const checkReasonText = (reason) => (reason ? CHECK_REASON_TEXT[reason] || '' : '')

/** Whether the last check came after the count it shows: that check could not count (see its reason). */
export const lastCheckIsNewerThanCount = (progress) => {
    const counted = Date.parse(progress?.count_checked_at)
    const checked = Date.parse(progress?.last_checked_at)
    return Number.isFinite(counted) && Number.isFinite(checked) && checked - counted > 1000
}

const PROPOSAL_TEXT = Object.freeze({
    threshold: 'The warm-up ad set has enough events. Expand now to create and switch on your audience ad sets.',
    stalled:
        'The warm-up has not reached the threshold in 28 days. You can expand anyway, keep warming, or cancel the warm-up.',
})

export const proposalText = (proposal) => PROPOSAL_TEXT[proposal?.kind] || PROPOSAL_TEXT.threshold

export const STEP_LABELS = Object.freeze({
    pending: 'Waiting to be created',
    ad_set_created: 'Created on Meta, paused',
    ads_verified: 'Ready to switch on',
    activated: 'Switched on',
    released_paused: 'Released, paused',
    skipped: 'Skipped',
})

export const SKIP_REASON_TEXT = Object.freeze({
    ads_rejected: 'Its ads were rejected by Meta',
    ad_set_rejected: 'Meta refused the ad set',
    no_deliverable_ads: 'It has no ad that can run',
    create_unverified: 'It may exist on Meta but could not be confirmed',
    row_deleted: 'It was deleted',
})

const PENDING_REASON_TEXT = Object.freeze({
    cap_raise: 'Budget change sent to Meta',
    cap_activate: 'Switch-on sent to Meta',
    warmup_activate: 'Switch-on sent to Meta',
    unconfirmed_graph_error: 'Meta did not confirm the change',
    unconfirmed_no_response: 'Meta did not answer',
    budget_shift: 'Budget being moved',
    budget_shift_unconfirmed: 'Budget move not confirmed',
})

export const pendingReasonText = (reason) => PENDING_REASON_TEXT[reason] || 'Change not confirmed by Meta'

export const LOWER_ONLY_NOTE =
    'The pixel warm-up is switched off for your licence, so the maximum daily budget cannot be raised. You can still lower it.'

export const ATTENTION_FALLBACK_TEXT = 'This needs your attention.'

export const FAILURE_TEXT = Object.freeze({
    switch_off: 'The pixel warm-up is switched off for your licence.',
    halted: 'Automatic steps are paused for all campaigns right now.',
    campaign_not_published: 'The campaign is not published.',
    campaign_budget_mode: 'The campaign uses one campaign budget.',
    no_maximum_daily_budget: 'The campaign has no maximum daily budget.',
    warmup_ad_set_missing: 'The warm-up ad set no longer exists on Meta.',
    connection_unusable: 'Your ad account connection could not be used.',
    needs_reauth: 'Your Meta connection needs to be reconnected.',
    read_failed: 'Meta could not be read.',
    rate_limited: 'Meta asked Turba to slow down.',
    cap_exceeded_live: 'The budgets on Meta would be above your maximum daily budget.',
    live_budget_unreadable: 'A budget on Meta could not be read.',
    no_rows_ready: 'None of the audience ad sets was ready.',
    rows_not_ready: 'Some audience ad sets were not ready.',
    interrupted: 'The expansion was interrupted several times.',
    warmup_pause_failed: 'The warm-up ad set could not be paused.',
    warmup_reactivated: 'The warm-up ad set was switched on again.',
    activation_unconfirmed: 'Meta did not confirm that an ad set was switched on.',
    cap_lock_busy: 'Another change to this campaign was being saved.',
    activation_refused: 'An ad set could not be switched on.',
    budget_change_unconfirmed: 'Meta did not confirm a budget change.',
})

export const failureText = (reason) => (reason ? FAILURE_TEXT[reason] || '' : '')

export const REFUSAL_FALLBACK_TEXT = Object.freeze({
    not_found: 'The warm-up was not found.',
    switch_off: 'The pixel warm-up is switched off for your licence.',
    halted: 'Automatic steps are paused right now.',
    version_changed: 'The warm-up changed meanwhile. The panel was refreshed; check it and try again.',
    not_allowed_in_state: 'This is not possible in the current state of the warm-up.',
    expansion_running: 'An expansion is running; try again when it has finished.',
    warmup_ad_set_missing: 'The warm-up ad set no longer exists on Meta.',
    automated_mode_not_allowed: 'Automated mode is not available for your licence.',
    unknown_mode: 'Unknown mode.',
    busy: 'Another change to this campaign is being saved right now; try again.',
})

export const refusalText = (result) =>
    result?.message || REFUSAL_FALLBACK_TEXT[result?.reason] || 'It did not work. Try again.'

export const DECISION_KIND_LABELS = Object.freeze({
    start_mode: 'Start of the campaign',
    event_selection: 'Event choice',
    warmup_proposal: 'Proposal',
    warmup_transition: 'Change of state',
    warmup_mode: 'Mode',
    warmup_cancel: 'Cancelled',
    warmup_attention: 'Needs attention',
    cap_pending: 'Change waiting for Meta',
    cap_reconciled_external: 'Change made on Meta',
    cap_over_maximum: 'Above the maximum',
    success_measure: 'Result of the warm-up',
    notice_dismissed: 'Notice closed',
})

export const decisionKindLabel = (kind) => DECISION_KIND_LABELS[kind] || kind

export const ACTOR_LABELS = Object.freeze({ owner: 'You', user: 'You', system: 'Turba', switches: 'Turba' })

export const actorLabel = (actor) => ACTOR_LABELS[actor] || actor || ''

/** The text a cancel answer tells the owner. */
export const describeCancelResult = (result) => {
    const parts = ['The warm-up was cancelled.']
    if (result.started_directly) {
        parts.push(
            `Your audience ad sets were launched directly${result.converted_rows ? ` (${result.converted_rows})` : ''}.`,
        )
    } else if (result.deleted) {
        parts.push(`${result.deleted} held audience ad set${result.deleted === 1 ? ' was' : 's were'} removed.`)
    }
    if (result.choice_applied === 'resume_warmup') parts.push('The warm-up ad set was switched on again.')
    if (result.choice_applied === 'leave_paused')
        parts.push('Nothing in this ad campaign will deliver until you switch an ad set on.')
    if (result.resume_failed_code) {
        parts.push('The warm-up ad set could not be switched on again; you can switch it on from the list.')
    }
    if (result.choice_reason) parts.push(String(result.choice_reason))
    if (result.cleanup_pending) parts.push('Some clean-up is still pending and will finish by itself.')
    return parts.join(' ')
}

// ---------------------------------------------------------------------------------------------------------------
// The "warm-up not used" notice

const NOT_USED_REASON_TEXT = Object.freeze({
    halted: 'New warm-ups are paused right now.',
    feature_off: 'The pixel warm-up is not available for your licence.',
    switch_unreadable: 'The warm-up settings of your licence could not be read.',
})

/** The sentence of the notice that the warm-up the owner chose was not used. */
export const warmupNotUsedText = (reason) =>
    `${NOT_USED_REASON_TEXT[reason] || 'The warm-up could not be used.'} The warm-up you chose was not used: the campaign was launched without it, with all its audiences.`

// ---------------------------------------------------------------------------------------------------------------
// Refusals of a budget change or a switch-on

const COUNTED_TAGS = (adSet) =>
    [adSet.warmup ? 'warm-up' : null, adSet.held ? 'held' : null, adSet.pending ? 'not confirmed by Meta' : null]
        .filter(Boolean)
        .join(', ')

/**
 * The text for an Apollo error of a budget change or switch-on that the maximum daily budget, a running expansion or an
 * unconfirmed change refused. `formatAmount` formats a minor-unit amount. Returns null for any other error.
 */
export const describeBudgetGuardError = (error, formatAmount = (amount) => String(amount)) => {
    const first = error?.graphQLErrors?.[0]
    const code = first?.extensions?.code
    if (code === 'DAILY_BUDGET_CAP_EXCEEDED') {
        const { maxDailyBudget, total, adSets } = first.extensions
        const counted = (adSets || [])
            .map((adSet) => {
                const tags = COUNTED_TAGS(adSet)
                return `${adSet.name}: ${formatAmount(adSet.budget)}${tags ? ` (${tags})` : ''}`
            })
            .join('; ')
        return [
            'This change would bring the daily budgets of the campaign above its maximum daily budget.',
            Number.isFinite(maxDailyBudget) && Number.isFinite(total)
                ? `Maximum ${formatAmount(maxDailyBudget)}, counted with this change ${formatAmount(total)}.`
                : null,
            counted ? `Counted: ${counted}.` : null,
            'If a counted ad set was deleted or changed in Meta, press Check again under the maximum daily budget of this campaign in the Ads Manager.',
        ]
            .filter(Boolean)
            .join(' ')
    }
    if (code === 'WARMUP_EXPANSION_RUNNING') {
        return first.message || 'An expansion is running; try again when it has finished.'
    }
    if (['DAILY_BUDGET_BUSY', 'BUDGET_CHANGE_UNCONFIRMED', 'AD_SET_BUDGET_BUSY', 'WARMUP_EXCLUSION'].includes(code)) {
        return first.message
    }
    return null
}
